import { PlusCircleSVG, TrashSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import Separator from 'components/common/separator';
import { FormState, Input } from 'components/elements';
import { Button, IconButton } from 'components/elements/buttons';
import { CustomLabel } from 'components/elements/custom-form-control';
import Text from 'components/elements/text';
import AirlineSelectInput from 'legacy-modules/master/airline/components/select-input';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { StyleObject } from 'styletron-standard';

import { onAddNewGuestAirline } from '../../hooks/use-get-default-values';
import { GuestFormType } from '../form-type';

export default function AirlineMemberCard() {
  const { t } = useTranslation();
  const { control } = useFormContext<GuestFormType>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'airlines',
    keyName: 'customId',
  });

  const isEmpty = fields.length === 0;

  const onAdd = () => append(onAddNewGuestAirline());
  const onRemove = (index: number) => remove(index);

  return (
    <FormState>
      {({ editable }) => (
        <CardSection style={{ paddingBottom: 0 }}>
          <FullContainer
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Text variant="HeadingXSmall">{t('guest:airline_member')}</Text>
            {editable && (
              <Button
                buttonType="tertiary"
                onClick={onAdd}
                startEnhancer={(color, size) => (
                  <PlusCircleSVG color={color} size={size} />
                )}
              >
                {t('common:add_extra', { extra: t('common:member') })}
              </Button>
            )}
          </FullContainer>
          <SeparatorVertical $margin={16} />
          {isEmpty && <Text variant="ParagraphLarge">{t('common:empty')}</Text>}
          {!isEmpty && (
            <LabelContainer>
              <HalfContainer>
                <CustomLabel label={t('common:airline')} required={editable} />
              </HalfContainer>
              <HalfContainer>
                <CustomLabel
                  label={t('common:member_number')}
                  required={editable}
                />
              </HalfContainer>
              <HalfContainer>
                <CustomLabel label={t('common:password')} />
              </HalfContainer>
              <HalfContainer>
                <CustomLabel label={t('common:note')} />
              </HalfContainer>
              {editable && <div style={{ width: 30 }} />}
            </LabelContainer>
          )}
          {fields.map((field, idx) => (
            <SectionContainer key={field.customId}>
              <HalfContainer>
                <CustomLabel
                  label={t('common:airline')}
                  required={editable}
                  $style={labelStyle}
                />
                <AirlineSelectInput
                  name={`airlines[${idx}].airlineId`}
                  label=""
                  placeholder={t('common:airline')}
                  required
                  hideRequiredStar
                />
              </HalfContainer>
              <Separator gap={16} direction="horizontal" />
              <HalfContainer>
                <CustomLabel
                  label={t('common:member_number')}
                  required={editable}
                  $style={labelStyle}
                />
                <Input
                  type="tel"
                  name={`airlines[${idx}].memberNumber`}
                  placeholder={t('common:member_number')}
                  required
                  hideRequiredStar
                />
              </HalfContainer>
              <Separator gap={16} direction="horizontal" />

              <HalfContainer>
                <CustomLabel label={t('common:password')} $style={labelStyle} />
                <Input
                  type="password"
                  name={`airlines[${idx}].password`}
                  placeholder={t('common:password')}
                />
              </HalfContainer>
              <Separator gap={16} direction="horizontal" />

              <HalfContainer>
                <CustomLabel label={t('common:note')} $style={labelStyle} />
                <Input
                  type="text"
                  name={`airlines[${idx}].note`}
                  placeholder={t('common:note')}
                />
              </HalfContainer>
              {editable && (
                <div
                  style={{
                    width: 30,
                    height: 32,
                    marginTop: 4,
                    marginBottom: 16,
                  }}
                >
                  <IconButton
                    buttonType="tertiary"
                    overrides={{
                      Root: {
                        style: {
                          height: '32px',
                          ':hover': {
                            backgroundColor: stylesColors.redLightest,
                            borderColor: stylesColors.redLightest,
                          },
                        },
                      },
                    }}
                    onClick={() => onRemove(idx)}
                  >
                    <TrashSVG color={stylesColors.redDefault} />
                  </IconButton>
                </div>
              )}
            </SectionContainer>
          ))}
          <SeparatorVertical $margin={16} />
        </CardSection>
      )}
    </FormState>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});

const LabelContainer = themedStyled(SectionContainer, ({ $theme }) => ({
  display: 'none',
  [$theme.mediaQuery.lg]: {
    display: 'flex',
  },
}));

const labelStyle: StyleObject | any = {
  '@media screen and (min-width: 992px)': {
    display: 'none',
  },
};
